/**
 * Imprint — provider identification.
 *
 * HealthPass is sold worldwide by one US company, so this is not a German
 * section 5 DDG entry with a commercial register and a VAT ID; it names the
 * company, where it is incorporated and how to reach a human. No officer is
 * named — a US company owes no such entry, and what a reader needs is a
 * reachable company rather than a person. EU readers arrive here expecting an
 * Impressum, so the page is still called Imprint and still answers the
 * question they came with, including who represents the company inside the
 * Union under Article 27 GDPR.
 */

import { LEGAL } from "./legalDetails";
import { LegalPageLayout, LegalSection } from "./LegalPageLayout";

export function ImprintPage() {
  const { operator, euRepresentative, contact, domain } = LEGAL;

  return (
    <LegalPageLayout
      title="Imprint"
      subtitle="Who operates HealthPass, and how to reach them."
      description="Provider identification for HealthPass: the operating company, its address, state of incorporation, representatives, EU representative and contact details."
      path="/imprint"
    >
      <LegalSection number={1} heading="Provider">
        <p>
          <strong>{operator.name}</strong>
          <br />
          {operator.street}
          <br />
          {operator.city}
          <br />
          {operator.country}
        </p>
        <p>
          Incorporated in: {operator.stateOfIncorporation}
          <br />
          Telephone: {operator.phone}
        </p>
        <p>
          This company is the contracting party for every customer, wherever
          they live.
        </p>
      </LegalSection>

      <LegalSection number={2} heading="Contact">
        <p>
          Email:{" "}
          <a
            className="text-primary-600 hover:underline"
            href={`mailto:${contact.general}`}
          >
            {contact.general}
          </a>
          <br />
          Website: https://{domain}
        </p>
      </LegalSection>

      <LegalSection number={3} heading="Representative in the EU (Article 27 GDPR)">
        <p>
          Travellers in the EU and EEA, and supervisory authorities, may address
          our representative in the Union instead of the company itself:
        </p>
        <p>
          {euRepresentative.name}
          <br />
          {euRepresentative.street}
          <br />
          {euRepresentative.city}
          <br />
          {euRepresentative.country}
          <br />
          <a
            className="text-primary-600 hover:underline"
            href={`mailto:${euRepresentative.email}`}
          >
            {euRepresentative.email}
          </a>
        </p>
      </LegalSection>

      <LegalSection number={4} heading="Data protection">
        <p>
          Questions about your data go to{" "}
          <a
            className="text-primary-600 hover:underline"
            href={`mailto:${contact.dpo}`}
          >
            {contact.dpo}
          </a>
          . What we hold and why is set out in the Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection number={5} heading="Online dispute resolution">
        <p>
          The European Commission provides a platform for online dispute
          resolution at{" "}
          <a
            className="text-primary-600 hover:underline"
            href="https://ec.europa.eu/consumers/odr"
            rel="noopener noreferrer"
            target="_blank"
          >
            ec.europa.eu/consumers/odr
          </a>
          . We are neither obliged nor willing to take part in dispute
          resolution proceedings before a consumer arbitration board.
        </p>
      </LegalSection>

      <LegalSection number={6} heading="Medical disclaimer">
        <p>
          HealthPass stores and presents health information that you enter or
          import. It is not a medical device, it does not diagnose, and it does
          not give medical advice. Nothing shown in the app replaces
          consultation with a qualified health professional. In an emergency,
          call your local emergency number.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default ImprintPage;
